import React from 'react';
import useField, { useFieldProps } from 'src/hooks/form/useField';
import AdditionalChargeDeclaration from './AdditionalChargesComponents/AdditionalChargeDeclaration';
import { ADDITIONAL_CHARGES_MESSAGES } from './AdditionalChargesComponents/AdditionalCharges.constants';
import { AdditionalChargesFieldValue } from './AdditionalChargesComponents/AdditionalCharges.types';

/**
 * Field hook for the `ADDITIONAL_CHARGES` task field type (ISS-291748 / C1) — Part 1 of the
 * Additional Vendor Charges flow.
 *
 * Mirrors `useRfqBids`. The validator is what enforces "at least one charge" — the task detail modal
 * already owns Submit (`SEModalWrapper`), so this field deliberately renders no Next/Cancel of its
 * own; a second commit affordance would compete with the modal's. Duplicate selection needs no rule
 * here: it is prevented by construction in the declaration component, which disables an
 * already-chosen charge in every other row.
 */
/**
 * Blocks the task's Submit until at least one charge is declared. Exported so the rule can be asserted
 * without standing up an antd form.
 */
export const validateAdditionalCharges = (
  rule: unknown,
  value: AdditionalChargesFieldValue | undefined,
  callback: (error?: string) => void
) => {
  if (value?.chargeCodes?.length) {
    return callback();
  }
  return callback(ADDITIONAL_CHARGES_MESSAGES.noChargeSelected);
};

const useAdditionalCharges = <T>(props: useFieldProps<T>) => {
  const formOptions = {
    rules: [{ validator: validateAdditionalCharges }],
  };

  const field = useField({
    ...props,
    formOptions,
  });

  const decorator = (child: React.ReactElement): React.ReactNode => {
    return field.decorator(React.createElement(AdditionalChargeDeclaration, { ...child.props }));
  };

  return {
    ...field,
    decorator,
  };
};

export default useAdditionalCharges;
